"use client";

import { motion, MotionValue } from "framer-motion";
import { useState, useEffect } from "react";
import Image from "next/image";
import ShadowCard from "@/components/common/ShadowCard";

export interface ServiceStepData {
  title: string;
  description: string;
  image1: string;
  image2: string;
}

interface DesktopServiceStepCardProps {
  step: ServiceStepData;
  index: number;
  scrollYProgress: MotionValue<number>;
  /** Custom thresholds array for when each section should appear */
  thresholds?: number[];
}

interface MobileServiceStepCardProps {
  step: ServiceStepData;
  index: number;
}

/**
 * Desktop Service Step Card with scroll-triggered animation
 */
export function DesktopServiceStepCard({
  step,
  index,
  scrollYProgress,
  thresholds = [0.2, 0.5, 0.75],
}: DesktopServiceStepCardProps) {
  const isEven = index % 2 === 0;
  const [showContent, setShowContent] = useState(false);
  const threshold = thresholds[index] || 0.8;

  useEffect(() => {
    const unsubscribe = scrollYProgress.on("change", (value) => {
      if (value >= threshold && !showContent) {
        setShowContent(true);
      }
    });
    return () => unsubscribe();
  }, [scrollYProgress, threshold, showContent]);

  return (
    <div className="relative grid grid-cols-2 items-center gap-16">
      {/* Text Content */}
      <motion.div
        initial={{ opacity: 0, x: isEven ? -50 : 50 }}
        animate={
          showContent
            ? { opacity: 1, x: 0 }
            : { opacity: 0, x: isEven ? -50 : 50 }
        }
        transition={{ duration: 0.8, ease: "easeOut", delay: 0.2 }}
        className={`bg-brand-black relative z-10 flex flex-col justify-center py-3 ${
          isEven ? "order-1 pr-8 text-left" : "order-2 pl-8 text-right"
        }`}
      >
        <h3 className="font-raleway mb-2.5 text-[32px] font-bold text-white">
          {step.title}
        </h3>
        <p className="font-raleway text-base leading-relaxed text-gray-300">
          {step.description}
        </p>
      </motion.div>

      {/* Images Container */}
      <motion.div
        initial={{ opacity: 0, x: isEven ? 50 : -50 }}
        animate={
          showContent
            ? { opacity: 1, x: 0 }
            : { opacity: 0, x: isEven ? 50 : -50 }
        }
        transition={{ duration: 0.8, ease: "easeOut", delay: 0.4 }}
        className={`relative z-10 flex ${
          isEven ? "order-2 justify-end" : "order-1 justify-start"
        }`}
      >
        <div className="relative">
          <motion.div
            initial={{ opacity: 0, scale: 0.9 }}
            animate={
              showContent
                ? { opacity: 1, scale: 1 }
                : { opacity: 0, scale: 0.9 }
            }
            transition={{ duration: 0.6, ease: "easeOut", delay: 0.5 }}
            className="relative h-[384px] w-[384px]"
          >
            <ShadowCard className="h-full w-full overflow-hidden">
              <Image
                src={step.image1}
                alt={step.title}
                fill
                className="object-cover"
              />
            </ShadowCard>
          </motion.div>
          <motion.div
            initial={{ opacity: 0, scale: 0.9 }}
            animate={
              showContent
                ? { opacity: 1, scale: 1 }
                : { opacity: 0, scale: 0.9 }
            }
            transition={{ duration: 0.6, ease: "easeOut", delay: 0.7 }}
            className={`absolute h-[290px] w-[290px] ${
              isEven
                ? "bottom-[-90px] left-[-90px]"
                : "right-[-90px] bottom-[-90px]"
            }`}
          >
            <ShadowCard className="h-full w-full overflow-hidden">
              <Image
                src={step.image2}
                alt={`${step.title} detail`}
                fill
                className="object-cover"
              />
            </ShadowCard>
          </motion.div>
        </div>
      </motion.div>
    </div>
  );
}

/**
 * Mobile Service Step Card (static, used within MobileStepWithPath)
 */
export function MobileServiceStepCard({
  step,
  index,
}: MobileServiceStepCardProps) {
  const isEven = index % 2 === 0;
  // Odd indices (1, 3, etc.) should be right-aligned to match path direction
  const isRightAligned = !isEven;

  return (
    <div className="relative grid grid-cols-1 items-center gap-10">
      {/* Text Content */}
      <div
        className={`bg-brand-black relative z-10 flex flex-col justify-center p-4 ${
          isRightAligned ? "text-right" : "text-left"
        }`}
      >
        <h3 className="font-raleway mb-2.5 text-xl font-bold text-white">
          {step.title}
        </h3>
        <p className="font-raleway text-base leading-relaxed text-gray-300">
          {step.description}
        </p>
      </div>

      {/* Images Container */}
      <div className="bg-brand-black relative z-10 flex justify-center">
        <div className="relative">
          <div className="relative h-[250px] w-[250px]">
            <ShadowCard className="h-full w-full overflow-hidden">
              <Image
                src={step.image1}
                alt={step.title}
                fill
                className="object-cover"
              />
            </ShadowCard>
          </div>
          <div
            className={`absolute h-[160px] w-[160px] ${
              isEven
                ? "bottom-[-40px] left-[-40px]"
                : "right-[-40px] bottom-[-40px]"
            }`}
          >
            <ShadowCard className="h-full w-full overflow-hidden">
              <Image
                src={step.image2}
                alt={`${step.title} detail`}
                fill
                className="object-cover"
              />
            </ShadowCard>
          </div>
        </div>
      </div>
    </div>
  );
}
